import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  signal,
} from "@angular/core";
import * as Highcharts from "highcharts";
import {
  HighchartsChartComponent,
  providePartialHighcharts,
} from "highcharts-angular";
import moment from "moment";
import { HC_LEGEND_STYLE } from "../../../charts/highcharts-shared";

export type AnnualMetric =
  | "annualized_return"
  | "annualized_sharpe"
  | "annualized_std_deviation";

@Component({
  selector: "app-exp-annual-performance-chart",
  standalone: true,
  imports: [HighchartsChartComponent],
  encapsulation: ViewEncapsulation.None,
  providers: [
    providePartialHighcharts({
      modules: () => [],
    }),
  ],
  template: `
    <div class="exp-ann-chart">
      @if (loaded() && hasData()) {
        <highcharts-chart
          [options]="chartOptions"
          [constructorType]="'chart'"
          style="width:100%; height:100%; display:block;"
        ></highcharts-chart>
      }
      @if (loaded() && !hasData()) {
        <div class="exp-ann-empty">No annual data available.</div>
      }
    </div>
  `,
  styles: [
    `
      .exp-ann-chart {
        width: 100%;
        min-height: 220px;
        position: relative;
      }
      .exp-ann-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        color: var(--text-faint);
        font-size: 12px;
      }
      .exp-ann-chart .highcharts-background {
        fill: transparent !important;
      }
      .exp-ann-chart .highcharts-plot-background {
        fill: transparent !important;
      }
      .exp-ann-chart .highcharts-grid .highcharts-grid-line {
        stroke: var(--border);
        stroke-dasharray: 2 3;
        opacity: 0.7;
      }
      .exp-ann-chart .highcharts-axis-line {
        stroke: var(--border) !important;
      }
      .exp-ann-chart .highcharts-tick {
        stroke: transparent !important;
      }
      .exp-ann-chart .highcharts-axis-labels text,
      .exp-ann-chart .highcharts-yaxis-labels text,
      .exp-ann-chart .highcharts-xaxis-labels text {
        fill: var(--text-dim) !important;
        font-family: var(--font-ui) !important;
        font-size: 11px !important;
        font-weight: 500 !important;
      }
      .exp-ann-chart .highcharts-data-label text,
      .exp-ann-chart .highcharts-data-label tspan {
        fill: var(--text) !important;
        font-family: var(--font-ui) !important;
        font-size: 10.5px !important;
        font-weight: 600 !important;
        text-shadow: none !important;
        stroke-width: 0 !important;
      }
      .exp-ann-chart .highcharts-legend-item text {
        fill: var(--text-dim) !important;
        font-family: var(--font-ui) !important;
        font-weight: 500 !important;
        font-size: 11.5px !important;
      }
      .exp-ann-chart .highcharts-legend-item:hover text {
        fill: var(--text) !important;
      }
      .exp-ann-chart
        .highcharts-legend-item.highcharts-legend-item-hidden
        text {
        fill: var(--text-faint) !important;
      }
      .exp-ann-chart .highcharts-credits {
        display: none;
      }
    `,
  ],
})
export class ExpAnnualPerformanceChartComponent implements OnInit, OnChanges {
  @Input() plotData: any = null;
  @Input() benchmark: any = null;
  @Input() portfolio: any = null;
  @Input() metric: AnnualMetric = "annualized_return";
  @Input() height: string | number = 220;

  loaded = signal(false);
  hasData = signal(false);
  chartOptions: Highcharts.Options = {};


  ngOnInit(): void {
    this.initPlot();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.plotData ||
      changes.benchmark ||
      changes.metric ||
      changes.portfolio
    ) {
      this.loaded.set(false);
      this.initPlot();
    }
  }

  private initPlot(): void {
    const raw = this.plotData?.[this.metric]?.Portfolio;
    if (!Array.isArray(raw) || !raw.length) {
      this.hasData.set(false);
      this.loaded.set(true);
      return;
    }

    // Mirror overview.component.ts: clamp both Portfolio and Benchmark to the
    // portfolio's effective date range. The backend may already restrict
    // start_date based on benchmark availability, which is why prod can show
    // fewer years than the raw plot data contains.
    const portfolioStart = this.portfolio?.start_date
      ? new Date(this.portfolio.start_date).valueOf()
      : Number.NEGATIVE_INFINITY;
    const portfolioEnd = this.portfolio?.end_date
      ? new Date(this.portfolio.end_date).valueOf()
      : Number.POSITIVE_INFINITY;

    const isPct = this.metric !== "annualized_sharpe";
    const portfolioScale = isPct ? 100 : 1;

    const map = new Map<
      number,
      { port: number | null; bench: number | null }
    >();

    for (const pt of raw) {
      const ts = typeof pt[0] === "number" ? pt[0] : Date.parse(String(pt[0]));
      if (!Number.isFinite(ts)) continue;
      if (ts < portfolioStart || ts > portfolioEnd) continue;
      const year = new Date(ts).getUTCFullYear();
      const value = (pt[1] || 0) * portfolioScale;
      map.set(year, { port: value, bench: map.get(year)?.bench ?? null });
    }

    const perfAnn = this.benchmark?.perfAnn || [];
    for (const b of perfAnn) {
      const year = Number(b.year);
      if (!Number.isFinite(year)) continue;
      const yearStart = moment.utc(year + "-01-01").unix() * 1000;
      if (yearStart < portfolioStart || yearStart > portfolioEnd) continue;
      let value: number | null = null;
      if (this.metric === "annualized_return") value = b.return ?? null;
      else if (this.metric === "annualized_sharpe") value = b.sharpe ?? null;
      else value = b.volatility ?? null;
      if (value === null || !Number.isFinite(value)) continue;
      const existing = map.get(year);
      if (existing) existing.bench = value;
      else map.set(year, { port: null, bench: value });
    }

    const entries = [...map.entries()].sort((a, b) => a[0] - b[0]);
    if (!entries.length) {
      this.hasData.set(false);
      this.loaded.set(true);
      return;
    }

    const categories = entries.map(([y]) => `'${String(y).slice(2)}`);
    const portfolioData = entries.map(([, v]) =>
      v.port === null ? null : +v.port.toFixed(2),
    );
    const benchmarkData = entries.map(([, v]) =>
      v.bench === null ? null : +v.bench.toFixed(2),
    );
    const hasAnyBenchmark = benchmarkData.some((v) => v !== null);

    this.hasData.set(true);
    this.initChart(categories, portfolioData, benchmarkData, hasAnyBenchmark);
  }

  private initChart(
    categories: string[],
    portfolioData: (number | null)[],
    benchmarkData: (number | null)[],
    hasBenchmark: boolean,
  ): void {
    const isSharpe = this.metric === "annualized_sharpe";
    const valueSuffix = isSharpe ? "" : "%";
    const decimals = isSharpe ? 2 : 1;
    const labelFormat = isSharpe ? "{y:.2f}" : "{y:.0f}%";
    const axisFormat = isSharpe ? "{value:.2f}" : "{value:.0f}%";
    const benchmarkName = this.benchmark?.index_name_short || "Benchmark";

    const series: any[] = [
      {
        name: "Portfolio",
        type: "column",
        data: portfolioData,
        borderRadius: 3,
        borderWidth: 0,
      },
    ];
    if (hasBenchmark) {
      series.push({
        name: benchmarkName,
        type: "column",
        data: benchmarkData,
        borderRadius: 3,
        borderWidth: 0,
      });
    }

    this.chartOptions = {
      chart: {
        type: "column",
        height: this.height as any,
        backgroundColor: "transparent",
        spacing: [12, 4, 8, 4],
        style: { fontFamily: "Inter, system-ui, sans-serif" },
      },
      credits: { enabled: false },
      title: { text: "" },
      xAxis: {
        categories,
        lineColor: "var(--border)",
        tickColor: "transparent",
        labels: { style: { color: "var(--text-dim)" } },
      },
      yAxis: {
        title: { text: "" },
        gridLineColor: "var(--border)",
        gridLineDashStyle: "ShortDash",
        gridLineWidth: 1,
        minorGridLineWidth: 0,
        labels: {
          format: axisFormat,
          style: { color: "var(--text-dim)" },
        },
        plotLines: [
          {
            value: 0,
            width: 1,
            color: "var(--border-strong)",
            zIndex: 4,
          },
        ],
      },
      legend: {
        enabled: true,
        align: "left",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemDistance: 18,
        margin: 12,
        padding: 0,
        symbolRadius: 2,
        symbolHeight: 8,
        symbolWidth: 14,
        squareSymbol: true,
        ...HC_LEGEND_STYLE,
      },
      tooltip: {
        borderRadius: 8,
        borderWidth: 1,
        padding: 10,
        shadow: false,
        shared: true,
        style: { color: "var(--text)" },
        formatter(this: any) {
          const points = (this.points || []) as any[];
          const year = `20${String(this.x).replace(/^'/, "")}`;
          let s = `<span style="font-size:10.5px;color:var(--text-dim);text-transform:uppercase;letter-spacing:0.06em;font-weight:500;">${year}</span>`;
          for (const p of points.filter(
            (p) => p.y !== null && p.y !== undefined,
          )) {
            const colorRef = `var(--highcharts-color-${p.colorIndex ?? 0})`;
            s += `<br/><span style="color:${colorRef}">\u25A0</span> <span style="color:var(--text-dim)">${p.series.name}:</span> <b>${Number(p.y).toFixed(decimals)}${valueSuffix}</b>`;
          }
          return s;
        },
      },
      plotOptions: {
        column: {
          groupPadding: 0.12,
          pointPadding: 0.05,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            format: labelFormat,
            inside: false,
            crop: false,
            overflow: "allow",
            style: {
              color: "var(--text)",
              fontWeight: "600",
              fontSize: "10.5px",
              textOutline: "none",
            },
          },
          states: {
            hover: { brightness: 0.08 },
          },
        },
        series: {
          animation: { duration: 350 },
        },
      },
      series,
    };

    setTimeout(() => {
      this.loaded.set(true);
    });
  }
}
